import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Divider from "components/divider";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Toggle from "components/form/toggle/Toggle";
import TimePick from "components/timePick/TimePick";
import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import { TFunction } from "i18next";
import "../teeSheetGeneral.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface IProps {
  state: IPutTeeSheetSettings;
  updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
  dates: { open_date: Date; close_date: Date };
  setDates: React.Dispatch<
    React.SetStateAction<{
      open_date: Date;
      close_date: Date;
    }>
  >;
}

const dropdownOptions = (t: TFunction) => [
  { label: t("Allow"), value: "allow" },
  { label: t("None"), value: "none" },
  { label: t("Allow within group"), value: "allow_within_group" },
];

const defaultBookingHolesOptions = (t: TFunction) => [
  { label: t("18 holes"), value: 18 },
  { label: t("9 holes"), value: 9 },
];

export default function BookingConfiguration(props: IProps) {
  const { state, updateState, dates, setDates } = props;
  const { t } = useTranslation();
  const { Option } = Select;

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    updateState(id as keyof IPutTeeSheetSettings, checked);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    updateState(id as keyof IPutTeeSheetSettings, value === "" || Number(value) < 0 ? "" : Number(value));
  }

  function isDateInThePast(date: Date) {
    const now = new Date();
    const isPast = date < now;

    if (isPast) {
      return true;
    }

    return false;
  }

  return (
    <div className="p-2">
      <FormLayout>
        <FormLayout.Group>
          <div className="open-close-date">
            <div>
              <span className="label-span">{t("Open date")}</span>
              <DatePickerInput
                startingDate={dates.open_date}
                setStartingDate={(date: Date) => setDates({ ...dates, open_date: date })}
                months={1}
                position="left"
              />
            </div>
          </div>
          <div className="open-close-date">
            <div>
              <span className="label-span">{t("Close date")}</span>
              <DatePickerInput
                startingDate={dates.close_date}
                setStartingDate={(date: Date) => setDates({ ...dates, close_date: date })}
                error={isDateInThePast(dates.close_date)} //If close date is in the past, warn user
                showBlurTitleOnError
                months={1}
                position="right"
              />
            </div>
          </div>
          <Input
            label={t("Maximum booking size")}
            type="number"
            id="max_booking_size"
            min={5}
            value={state.max_booking_size}
            onChange={handleInputChange}
            tooltipText={t("Set the maximum players allowed in a single booking")}
          />
        </FormLayout.Group>
      </FormLayout>
      <Divider />
      <FormLayout>
        <FormLayout.Group alignStart>
          <Input
            label={t("Days in advance start")}
            type="number"
            id="default_days_in_advance_start"
            value={state.default_days_in_advance_start}
            onChange={handleInputChange}
            suffix={t("Days")}
            helpText={t("The soonest a customer can book a tee time (eg: 2 days prior)")}
          />
          <Input
            label={t("Days in advance end")}
            type="number"
            id="default_days_in_advance_end"
            value={state.default_days_in_advance_end}
            onChange={handleInputChange}
            suffix={t("Days")}
            helpText={t("The furthest out a customer can book a tee time")}
          />
          <TimePick
            value={state.default_open_time}
            label={t("Default open time")}
            onChange={timeString => updateState("default_open_time", timeString)}
            size="large"
            helpText={t("Time of day new booking slots become visible")}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Input
            label={t("Minimum time between bookings")}
            type="number"
            id="booking_buffer"
            value={state.booking_buffer}
            onChange={handleInputChange}
            suffix={t("Minutes")}
            tooltipText={t(
              "Minimum time between bookings (in minutes) for public booking site. Leaving this empty will result in no restriction..",
            )}
            min={0}
          />
        </FormLayout.Group>
      </FormLayout>
      <Divider />
      <FormLayout>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Highlight new bookings")}
            id="highlight_new_bookings"
            checked={!!state.highlight_new_bookings || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        {state.highlight_new_bookings ? (
          <FormLayout.Group>
            <Input
              value={state.highlight_new_bookings_window || ""}
              label={t("Highlight new bookings window")}
              id="highlight_new_bookings_window"
              onChange={handleInputChange}
              placeholder={"2"}
              suffix={"Hours"}
              tooltipText={t("Set maximum number of hours a recent booking highlight appears on the tee sheet")}
            />
          </FormLayout.Group>
        ) : null}
      </FormLayout>
      <Divider />
      <Toggle
        labelRight={t("Allow guest names for bookings")}
        id="bookings_allow_guest_names"
        checked={!!state.bookings_allow_guest_names || false}
        onChange={handleToggleChange}
      />
      <Divider />
      <p className="ui-tee-sheet-general-section-title">
        {t("Booking Type")}{" "}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="selectnew-tooltip" className="ui-select_element_tooltip">
              {t(
                "Choose which booking type customers can book online and whether they can start a new tee time or only join an existing booking",
              )}
            </Tooltip>
          }
        >
          <FontAwesomeIcon className="ui-tee-sheet-general-section-tooltip" icon={faCircleQuestion} />
        </OverlayTrigger>
      </p>
      <br />
      <FormLayout>
        <FormLayout.Group>
          <Select
            label={t("Single Bookings")}
            defaultValue={state.single_bookings}
            onChange={(value: string) => updateState("single_bookings", value)}
          >
            {dropdownOptions(t).map((option, index) => (
              <Option key={index} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Select
            label={t("Twosome Bookings")}
            defaultValue={state.twosome_bookings}
            onChange={(value: string) => updateState("twosome_bookings", value)}
          >
            {dropdownOptions(t).map((option, index) => (
              <Option key={index} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FormLayout.Group>
        <FormLayout.Group>
          <Select
            label={t("Threesome Bookings")}
            defaultValue={state.threesome_bookings}
            onChange={(value: string) => updateState("threesome_bookings", value)}
          >
            {dropdownOptions(t).map((option, index) => (
              <Option key={index} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Select
            label={t("Foursome Bookings")}
            defaultValue={state.foursome_bookings}
            onChange={(value: string) => updateState("foursome_bookings", value)}
          >
            {dropdownOptions(t)
              .filter(option => option.value !== "allow_within_group")
              .map((option, index) => (
                <Option key={index} value={option.value}>
                  {option.label}
                </Option>
              ))}
          </Select>
        </FormLayout.Group>
      </FormLayout>
      <Divider />
      <Toggle
        labelRight={t("Single booking per tee time")}
        id="prevent_multiple_bookings"
        checked={!!state.prevent_multiple_bookings || false}
        onChange={handleToggleChange}
        labelSupport={t("Lock remaining available spots once a tee time is booked.")}
      />
      <Divider />
      <FormLayout>
        <FormLayout.Group>
          <Select
            label={t("Default holes for new bookings")}
            defaultValue={state.default_booking_holes ?? 18}
            onChange={(value: number) => updateState("default_booking_holes", value)}
            helpText={t("Number of holes selected by default when booking a new tee time from the tee sheet")}
          >
            {defaultBookingHolesOptions(t).map((option, index) => (
              <Option key={index} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FormLayout.Group>
      </FormLayout>
      <Divider />
      <p className="ui-tee-sheet-general-section-title">{t("Booking Reminders")}</p>
      <br />
      <FormLayout>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Enable booking reminder emails")}
            id="enable_booking_reminder_emails"
            checked={!!state.enable_booking_reminder_emails}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        {state.enable_booking_reminder_emails || state.enable_booking_reminder_texts ? (
          <FormLayout.Group>
            <Input
              label={t("Hours before tee time")}
              type="number"
              id="booking_reminder_hours"
              value={state.booking_reminder_hours ?? 24}
              onChange={handleInputChange}
              min={1}
              suffix={t("Hours")}
              tooltipText={t("Send booking reminders this many hours before the tee time")}
            />
          </FormLayout.Group>
        ) : null}
      </FormLayout>
    </div>
  );
}
